import { useState } from 'react'
import type { FC } from 'react'
import { Link as RouterLink, useNavigate, useParams } from 'react-router'
import {
  alpha,
  Box,
  Button,
  Link,
  MenuItem,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined'
import { DataGrid, type GridColDef, type GridRowParams } from '@mui/x-data-grid'
import {
  type MembersQueueRow,
  type Product,
  useMembersQueue,
  useTeamGroups,
} from './MembersDashboardService'
import { ManageMembersModal } from '~/entities/assignee-group'
import { BLUE, ORANGE, ORANGE_DARK } from '~/shared/lib/theme'
import ContractFilter from './ContractFilter'
import { MemberAvatar } from './memberDisplay'
import ProductToggle from './ProductToggle'
import { overviewPath, useDashboardScope } from './queueRoutes'

const PAGE_SIZE = 25

// Product pill background/foreground tones, keyed by the product label the BFF sends.
const PRODUCT_STYLES: Record<string, { bg: string; color: string }> = {
  'Care Only': { bg: BLUE[50], color: BLUE[700] },
  'Health Plan': { bg: alpha(ORANGE, 0.12), color: ORANGE_DARK },
}

const ProductChip: FC<{ product: string }> = ({ product }) => {
  const style = PRODUCT_STYLES[product] ?? { bg: 'transparent', color: 'inherit' }
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-block',
        px: 1.25,
        py: 0.25,
        borderRadius: 5,
        fontSize: 12,
        // Data grid v7 dropped the `.MuiDataGrid-cellContent` wrapper, so
        // `renderCell` output now inherits the cell's line-height — which the
        // grid sets to the row height to centre plain text. Without an explicit
        // value the pill stretches to the full row.
        lineHeight: 1.5,
        fontWeight: 500,
        bgcolor: style.bg,
        color: style.color,
        whiteSpace: 'nowrap',
      }}
    >
      {product}
    </Box>
  )
}

// Fallback titles for the loading state; the server provides the real title
// (including the measure name for care-gap queues) in the response.
const QUEUE_TITLES: Record<string, string> = {
  overdue_cases: 'Overdue cases',
  members_awaiting_reply: 'Members awaiting reply',
  onboarding_blocked: 'Onboarding blocked',
  hp_not_signed_up: 'Health plan · not signed up',
}

// Queues that reach a team (Case.owner_group) and so support the ?owner_group filter.
// Mirrors CASE_BACKED_QUEUES on the backend.
const TEAM_FILTERABLE_QUEUES = new Set(['overdue_cases', 'members_awaiting_reply'])

/** @knipskip - used in-file and asserted by the drilldown test; not part of the feature's API. */
export const queueTitle = (key: string): string =>
  QUEUE_TITLES[key] ?? (key.startsWith('gap_') ? 'Care gap' : key)

// Deep-link each queue's rows to the profile tab most relevant to that queue. Rows
// carry the person_id; `/people/<id>/<tab>` resolves to the patient profile while
// preserving the sub-path (see InterfaceSwitch → PersonDetail). Unknown keys fall back
// to the profile tab.
const QUEUE_ROW_TAB: Record<string, string> = {
  hp_not_signed_up: 'profile',
  onboarding_blocked: 'onboarding',
  overdue_cases: 'todos',
  members_awaiting_reply: 'todos',
}

/** @knipskip - used in-file and asserted by the drilldown test; not part of the feature's API. */
export const memberRowPath = (
  queueKey: string,
  personId: number,
  caseId?: number | null,
): string => {
  // A care-gap queue (`gap_*`) row carries no `case_id` to deep-link, so it goes to the
  // Care Gaps tab, which lists every measure for the member with its status and case link.
  const tab = queueKey.startsWith('gap_') ? 'care-gaps' : (QUEUE_ROW_TAB[queueKey] ?? 'profile')
  const base = `/people/${personId}/${tab}`
  // The To Dos tab selects a case from `?caseId=` (see useManageSelectedTodo), so
  // deep-link the case-backed queues straight to the case that put the member on the
  // list instead of dropping ops into the full open-case list.
  return tab === 'todos' && caseId != null ? `${base}?caseId=${caseId}` : base
}

// Annotated here rather than only on COLUMNS below: `.map()` drops contextual
// typing, so without this the column callbacks infer as `any` and a library
// signature change (as in the data-grid v7 valueGetter rework) slips past tsc.
const COLUMN_DEFS: GridColDef<MembersQueueRow>[] = [
  {
    field: 'name',
    headerName: 'Member',
    flex: 1.5,
    valueGetter: (_value, row) => `${row.first_name ?? ''} ${row.last_name ?? ''}`.trim(),
    renderCell: params => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <MemberAvatar
          id={params.row.id}
          firstName={params.row.first_name}
          lastName={params.row.last_name}
        />
        <span>{params.value}</span>
      </Box>
    ),
  },
  {
    field: 'product',
    headerName: 'Product',
    flex: 1,
    renderCell: params => <ProductChip product={params.row.product} />,
  },
  { field: 'state', headerName: 'State', flex: 0.6 },
  { field: 'complexity', headerName: 'Complexity', flex: 0.8 },
  { field: 'last_active', headerName: 'Last active', flex: 0.8 },
  { field: 'reason', headerName: 'On list because', flex: 1.8 },
]

// The grid is server-paginated but the queues endpoint has no ordering param,
// so header sorting would only reorder the visible 25-row page — misleading on a
// full queue. Disable it until server-side sort lands.
const COLUMNS: GridColDef<MembersQueueRow>[] = COLUMN_DEFS.map(col => ({
  sortable: false,
  ...col,
}))

// Skeleton rows for the grid's first load — column widths mirror COLUMNS' flex so
// the placeholder lines up with the real table. Shown via the DataGrid loadingOverlay
// slot; on page changes `keepPreviousData` keeps `isPending` false, so this only
// appears on the initial fetch (empty grid), not on pagination.
const GridLoadingSkeleton: FC = () => (
  <Box sx={{ width: '100%', px: 2, pt: 1 }}>
    {Array.from({ length: 8 }).map((_, i) => (
      <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.15 }}>
        <Skeleton variant="circular" width={28} height={28} sx={{ flexShrink: 0 }} />
        <Skeleton variant="text" sx={{ flex: 1.5 }} />
        <Skeleton variant="text" sx={{ flex: 1 }} />
        <Skeleton variant="text" sx={{ flex: 0.6 }} />
        <Skeleton variant="text" sx={{ flex: 0.8 }} />
        <Skeleton variant="text" sx={{ flex: 0.8 }} />
        <Skeleton variant="text" sx={{ flex: 1.8 }} />
      </Box>
    ))}
  </Box>
)

const MembersQueueDrilldown: FC = () => {
  // The route only matches with `:key` present; the default just narrows the type.
  const { key = '' } = useParams()
  const navigate = useNavigate()
  const { product, setProduct, contract, setContract } = useDashboardScope()
  const [page, setPage] = useState(0)
  const [ownerGroup, setOwnerGroup] = useState<number | ''>('')
  const [manageOpen, setManageOpen] = useState(false)

  const handleProductChange = (next: Product) => {
    setPage(0)
    setProduct(next)
  }
  const handleContractChange = (next: number | null) => {
    setPage(0)
    setContract(next)
  }

  const teamFilterable = TEAM_FILTERABLE_QUEUES.has(key)
  const { data: teams } = useTeamGroups(teamFilterable)
  const selectedTeam = ownerGroup === '' ? undefined : (teams ?? []).find(t => t.id === ownerGroup)
  const { data, isPending: isLoading } = useMembersQueue(
    key,
    product,
    page,
    PAGE_SIZE,
    teamFilterable && ownerGroup !== '' ? ownerGroup : undefined,
    contract,
  )

  return (
    <Box
      sx={{
        p: 4,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.default',
      }}
    >
      <Link
        component={RouterLink}
        to={overviewPath(product, contract)}
        underline="hover"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          fontSize: 13,
          fontWeight: 500,
        }}
      >
        <ArrowBackIcon sx={{ fontSize: 16 }} />
        Overview
      </Link>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          mt: 1.5,
          mb: 2.5,
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 500 }}>
            {data?.title ?? queueTitle(key)}
          </Typography>
          {data?.subtitle ? (
            <Typography variant="body2" sx={{ color: 'grey.500', mt: 0.5 }}>
              {data.subtitle}
            </Typography>
          ) : null}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {teamFilterable && (
            <>
              <TextField
                select
                size="small"
                label="Team"
                value={ownerGroup}
                onChange={e => {
                  setOwnerGroup(e.target.value === '' ? '' : Number(e.target.value))
                  setPage(0)
                }}
                sx={{ minWidth: 200 }}
              >
                <MenuItem value="">All teams</MenuItem>
                {(teams ?? []).map(team => (
                  <MenuItem key={team.id} value={team.id}>
                    {team.name}
                  </MenuItem>
                ))}
              </TextField>
              {/* Manage the team you just filtered by, without leaving the queue. Not switch-gated:
                  the members dashboard is already flag-limited to managers/leads, the intended
                  audience for team management. Enabled once a specific team is picked. */}
              <Tooltip title={ownerGroup === '' ? 'Select a team to manage its members' : ''}>
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<GroupsOutlinedIcon />}
                    disabled={ownerGroup === ''}
                    onClick={() => setManageOpen(true)}
                    data-testid="manage-team-members"
                  >
                    Manage members
                  </Button>
                </span>
              </Tooltip>
            </>
          )}
          <ContractFilter value={contract} onChange={handleContractChange} />
          <ProductToggle value={product} onChange={handleProductChange} />
        </Box>
      </Box>

      <Box sx={{ flex: 1, minHeight: 400 }}>
        <DataGrid
          rows={data?.results ?? []}
          columns={COLUMNS}
          loading={isLoading}
          slots={{ loadingOverlay: GridLoadingSkeleton }}
          rowCount={data?.count ?? 0}
          pagination
          paginationMode="server"
          paginationModel={{ page, pageSize: PAGE_SIZE }}
          pageSizeOptions={[PAGE_SIZE]}
          onPaginationModelChange={model => setPage(model.page)}
          onRowClick={(params: GridRowParams<MembersQueueRow>) =>
            void navigate(memberRowPath(key, Number(params.id), params.row.case_id))
          }
          disableColumnFilter
          disableColumnMenu
          sx={{
            border: '1px solid',
            borderColor: 'grey.100',
            borderRadius: 2,
            bgcolor: 'background.default',
            '& .MuiDataGrid-columnHeaders': { bgcolor: 'grey.50' },
            '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 600 },
            '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
            '& .MuiDataGrid-row:hover': { cursor: 'pointer' },
          }}
        />
      </Box>

      <ManageMembersModal
        open={manageOpen}
        onClose={() => setManageOpen(false)}
        groupId={ownerGroup === '' ? null : ownerGroup}
        groupName={selectedTeam?.name}
      />
    </Box>
  )
}

export default MembersQueueDrilldown
